import { useState, useEffect } from 'react';

import { Alert, Button, Modal, Table, Typography, Collapse, Skeleton, Tag } from 'antd';
import { LeftCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import _ from 'lodash';

import { formatDate, formatPhoneNumber, formatZipCode, getDisplayCompanyId } from 'utils/helpers';
import { Color, StatusCode } from 'utils/enums';
import { diagRefCode, H5499_POS_CODES, ORGANIZATION_NAME } from 'utils/objects';

import { AuthDiags } from 'store/authorization/portal/actions';

import AuthorizationDetailModalProps from './Proptypes';

import './index.scss';

const { Title } = Typography;

const AuthorizationDetailModal = (props: AuthorizationDetailModalProps) => {
  const [referralType, setReferralType] = useState('');
  const [showHospitalDates, setShowHospitalDates] = useState(false);
  const referenceColumns = [
    {
      title: 'REFERENCE',
      key: 'RmiDiagRefNo',
      dataIndex: 'RmiDiagRefNo',
      render: (_: number, record: any) => {
        if(record.RmiDiag !== 'RR' && record.RmiDiag !== 'MR' && record.RmiDiag !== 'RA' && record.RmiDiag !== 'DR' && record.RmiDiag !== 'UR') {
          return record.RmiDiagRefNo;
        }
      }
    },
    {
      title: 'DIAG CODE',
      key: 'RmiDiag',
      dataIndex: 'RmiDiag',
      render: (_: number, record: any) => {
        if(record.RmiDiag !== 'RR' && record.RmiDiag !== 'MR' && record.RmiDiag !== 'RA' && record.RmiDiag !== 'DR' && record.RmiDiag !== 'UR') {
          return record.RmiDiag;
        }
      }
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDiagDesc',
      dataIndex: 'RmiDiagDesc',
      render: (_: number, record: any) => {
        if(record.RmiDiag !== 'RR' && record.RmiDiag !== 'MR' && record.RmiDiag !== 'RA' && record.RmiDiag !== 'DR' && record.RmiDiag !== 'UR') {
          return record.RmiDiagDesc;
        }
      }
    },
  ];

  const cptCodeColumns = [
    {
      title: 'CPT CODE',
      key: 'RmiProcCode',
      dataIndex: 'RmiProcCode',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDescr',
      dataIndex: 'RmiDescr',
    },
    {
      title: 'MODIFIER',
      key: 'RmiModif',
      dataIndex: 'RmiModif',
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagCode',
      dataIndex: 'RmiDiagCode',
    },
    {
      title: 'QUANTITY',
      key: 'RmiQty',
      dataIndex: 'RmiQty',
    },
  ];

  const noteColumns = [
    {
      title: 'SUBJECT',
      key: 'RmiSubject',
      dataIndex: 'RmiSubject',
      render: (__: number, record: any) => {
        //hide LETTER ADDRESS
        if(record.RmiSubject.toUpperCase() === 'LETTER ADDRESS') {
          return;
        }
        return record.RmiSubject;
      }
    },
    {
      title: 'NOTES',
      key: 'RmiNotes',
      dataIndex: 'RmiNotes',
      render: (__: number, record: any) => {
        let notes = record.RmiNotes;
        if(record.RmiSubject.toUpperCase() === 'SYMPTOMS AND TREATMENTS') {
          //this is the exact code taken from the legacy code to have this special format on line breaks because ReactQuill cannot format this
          let string = "";
          let array = record.RmiNotes.split(/\n/);
          if (array[array.length - 1].indexOf("GMT-0") > -1) {
            let date = array[array.length - 1];
            const d = date.split(/\s/);
            date = d[0] + " " + d[1] + " " + d[2] + " " + d[3] + " at " + d[4];

            let userId = array[array.length - 2];
            string = array.toString();
            array = string.replace(/,/gi, String.fromCharCode(13) + String.fromCharCode(10)).replace(/^,/, "");
            notes = array + String.fromCharCode(13) + String.fromCharCode(10) + "Submitted by " + userId + " on " + date + ".";
          }
        }
        //hide LETTER ADDRESS
        else if(record.RmiSubject.toUpperCase() === 'LETTER ADDRESS') {
          return;
        }
        return (
          <div style={{whiteSpace: "pre-line", textOverflow: "ellipsis"}}>
            {_.unescape(notes)}
          </div>
        )
      }
    },
  ];

  useEffect(() => {
    if(props.authorizationDetailModal.AuthDiags) {
      setReferralType(props.authorizationDetailModal.AuthDiags.find((item: AuthDiags) => diagRefCode.includes(item.RmiDiag))?.RmiDiagDesc);
    }
    checkHospitalDates(props.authorizationDetailModal);
  }, [props.authorizationDetailModal]);

  const closeBothModals = () => {
    props.isModalVisibleHandler();
    props.isAuthHistoryModalVisibleHandler();
  }

  const checkHospitalDates = (authModal: any) => {
    let H5499Found = false;
    if (authModal.RmiPOS && H5499_POS_CODES.includes(authModal.RmiPOS.trim())) {
      H5499Found = authModal.AuthDetails.some((detail: any) => detail.RmiProcCode && detail.RmiProcCode.trim() === "H5499");

        //03/17/17 changed spec per meeting with CM, UM, and Dr. Wang
        if (authModal.RmiAuthDate && !H5499Found) {
            setShowHospitalDates(false);
        }
        else if (H5499Found && authModal.RmiSvcType !== "65") {
            setShowHospitalDates(true);
            authModal.AuthDetails.forEach((detail: any) => {
              if (detail.RmiProcCode != null) {
                var procCode = detail.RmiProcCode;
                //get admit and discharge date from detail line
                if (procCode.trim() === "H5499") {
                  authModal.RmiAdmitDate = detail.RmiAdmitDate;
                  authModal.RmiDschDate = detail.RmiDschDate;
                }
              }
            })
        }
    } else {
        setShowHospitalDates(false);
    }
  };

  /**
   * hide - eligibility detail sections, bottom tabs
   * show - the modal content only for print
   */
  const print = () => {
    $(".print-eligibility-member-info, .print-member-info, .print-tab-section, .condition-code-table, .title, .print-logo").removeClass("include-print").addClass("exclude-print");
    $(".print-authorization-detail-modal").removeClass('exclude-print').addClass("include-print");
    window.print();
  }

  return (
    <>
      <Modal
        className="print-authorization-detail-modal"
        visible={props.isModalVisible}
        width={1000}
        onCancel={props.isModalVisibleHandler}
        footer={[
          <>
            <div className="row pt-2">
              <div className="col-lg-12 col-md-12">
                <p className="float-left pt-2">&copy; {ORGANIZATION_NAME}, Inc., All Rights Reserved</p>
              </div>
            </div>
          </>
        ]}
      >
        <div className="print-authorization-detail-section">
          <Title
            className="text-center"
            level={4}
          >
            Authorization Detail
          </Title>

          <hr className="print-display authorization-detail-form-modal-divider" />

          {props.isFetchingAuth ? (
            <>
              <Skeleton
                active
                paragraph={{ rows: 4 }}
              />
              <br />
              <Skeleton
                active
                paragraph={{ rows: 8 }}
              />
              <br />
              <Skeleton
                active
                paragraph={{ rows: 4 }}
              />
              <br />
              <Skeleton
                active
                paragraph={{ rows: 4 }}
              />
            </>
          ) : (
            <>
              <div className="row">
                <div className="col-lg-24 float-right">
                  <Button type="primary" className="back-button" style={{ border: '#f8ac59', backgroundColor: '#f8ac59', float: 'left' }} onClick={props.isModalVisibleHandler} icon={<LeftCircleOutlined />}>
                    Back
                  </Button>
                  <Button type="primary" className="print-button" style={{ float: 'right' }} onClick={print} icon={<PrinterOutlined />}>
                    Print
                  </Button>
                </div>
              </div>

              <hr className="authorization-detail-form-modal-divider" />

              <div className="authorization-detail-form-modal flex-lg-wrap justify-content-start ml-0 mr-3">
                <div className="row">
                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Auth Number:</th>
                          <td>{getDisplayCompanyId(props.authorizationDetailModal.RmiCompanyId)} - {props.authorizationDetailModal.RmiAuthNo}</td>
                        </tr>
                                         
                        {showHospitalDates ? (
                          <>                    
                            <tr key={3}>
                              <th>Admit Date:</th>
                              <td>{formatDate(props.authorizationDetailModal.RmiAdmitDate, 'MM/DD/YYYY')}</td>
                            </tr>
                          </>
                        ) : 
                        (<>
                          <tr>
                            <th className="bold">Request Date:</th>
                            <td>{formatDate(props.authorizationDetailModal.RmiReqDate, 'MM/DD/YYYY')}</td>
                          </tr>
                          <tr>
                            <th className="bold">Referral Type:</th>
                            <td>{referralType}</td>
                          </tr>
                        </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Status:</th>
                            <td>
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'APPROVED' && <Tag color={Color.APPROVED}>APPROVED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'MODIFIED' && <Tag color={Color.MODIFIED}>MODIFIED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'DEFERRED' && <Tag color={Color.DEFERRED}>DEFERRED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'CANCELLED' && <Tag color={Color.CANCELLED}>CANCELLED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'EXPIRED' && <Tag color={Color.EXPIRED}>EXPIRED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'DENIED' && <Tag color={Color.DENIED}>DENIED</Tag>}
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'PENDED' && <Tag color={Color.PENDED}>PENDED</Tag>}
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'REQUESTED' && <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> }
                              { StatusCode[props.authorizationDetailModal.RmiStatus] === 'SYSTEM_HOLD' && <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> }
                          </td>
                        </tr>
                        {showHospitalDates ? (
                          <>                    
                            <tr key={7}>
                              <th>Discharge Date:</th>
                              <td>{formatDate(props.authorizationDetailModal.RmiDschDate, 'MM/DD/YYYY')}</td>
                            </tr>
                          </>
                          ) : (
                          <>
                            <tr>
                              <th className="bold">Expiration Date:</th>
                              <td>{formatDate(props.authorizationDetailModal.RmiExprDate, 'MM/DD/YYYY')}</td>
                            </tr>
                            <tr>
                              <th className="bold">Retro Date:</th>
                              <td>{referralType !== 'RETROACTIVE REFERRAL' ? '' : formatDate(props.authorizationDetailModal.RmiAuthDate, 'MM/DD/YYYY')}</td>
                            </tr>
                          </>
                          )
                        }
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <div className="row">
                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Patient Name:</th>
                          <td>{props.authorizationDetailModal.RmiMembName}</td>
                        </tr>
                        <tr>
                          <th className="bold">Address:</th>
                          <td>{props.authorizationDetailModal.RmiMembaddrStreet}</td>
                        </tr>
                        <tr>
                          <th className="bold">City, State, Zip:</th>
                          <td>{props.authorizationDetailModal.RmiMembaddrCity}{props.authorizationDetailModal.RmiMembaddrStreet ? ',' : ''} {props.authorizationDetailModal.RmiMembaddrState} {props.authorizationDetailModal.RmiMembaddrZip}</td>
                        </tr>
                        <tr>
                          <th className="bold">Health Plan:</th>
                          <td>{props.authorizationDetailModal.RmiHpName}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Date of Birth:</th>
                          <td>{formatDate(props.authorizationDetailModal.RmiBirth, 'MM/DD/YYYY')}</td>
                          <th className="bold">Gender:</th>
                          <td className="gender">{props.authorizationDetailModal.RmiSex}</td>
                        </tr>
                        <tr>
                          <th className="bold">Member ID:</th>
                          <td>{props.authorizationDetailModal.RmiMembId}</td>
                        </tr>
                        <tr>
                          <th className="bold">Member PCP:</th>
                          <td>{props.authorizationDetailModal.RmiPcpRevFullName}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <div className="row">
                  <div className="col-lg-24 col-md-24">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Request Provider:</th>
                          <td>{props.authorizationDetailModal.RmiRefRevFullName}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <div className="row">
                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Referral To:</th>
                          <td>{props.authorizationDetailModal.RmiReqRevFullName}</td>
                        </tr>
                        <tr>
                          <th className="bold">Specialty:</th>
                          <td>{props.authorizationDetailModal.RmiReqSpec}</td>
                        </tr>
                        <tr>
                          <th className="bold">POS:</th>
                          <td>{props.authorizationDetailModal.RmiPlaceSvc}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="col-lg-12 col-md-12 col-print-6">
                    <table className="test" style={{ textAlign: "left" }}>
                      <tbody>
                        <tr>
                          <th className="bold">Address:</th>
                          <td>{props.authorizationDetailModal.RmiSpecaddrStreet} <br />
                            {props.authorizationDetailModal.RmiSpecaddrCity}{props.authorizationDetailModal.RmiSpecaddrStreet ? ',' : ''} {props.authorizationDetailModal.RmiSpecAddrState} {formatZipCode(props.authorizationDetailModal.RmiSpecaddrZip)}
                          </td>
                        </tr>
                        <tr>
                          <th className="bold">Phone Number:</th>
                          <td>{formatPhoneNumber(props.authorizationDetailModal.RmiSpecaddrPhone)}</td>
                        </tr>
                        <tr>
                          <th className="bold">Fax Number:</th>
                          <td>{formatPhoneNumber(props.authorizationDetailModal.RmiSpecaddrFax)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <div className="col-lg-24 mb-4">
                  <Table
                    className="diagnosis-table"
                    columns={referenceColumns}
                    dataSource={props.authorizationDetailModal.AuthDiags}
                    pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
                  />
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <div className="col-lg-24">
                  <Table
                    className="cptcode-table"
                    columns={cptCodeColumns}
                    dataSource={props.authorizationDetailModal.AuthDetails}
                    pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
                  />
                </div>

                <hr className="authorization-detail-form-modal-divider" />

                <Collapse
                  defaultActiveKey={['1']}
                  expandIconPosition={'right'}
                >
                  <Collapse.Panel header="Notes" key="1">
                    <Table
                      className="notes-table"
                      columns={noteColumns}
                      dataSource={props.authorizationDetailModal.AuthNotes}
                      pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
                    />
                  </Collapse.Panel>
                </Collapse>

                <hr className="authorization-detail-form-modal-divider" />

                <Title level={5}>
                  Attachments:
                </Title>

                <ul>
                  {props.attachments.map((data: any, key: number) => {
                    return <li key={key}>{data[4]}</li>;
                  })
                  }
                </ul>

                <div className="col-lg-24 mb-4">
                  <Alert
                    className="provider-info"
                    message="Provider&#8217;s Responsibilities"
                    description={
                      <>
                        Eligibility must be verified 24 hours before the actual date of service.
                        Provider MUST confirm with Health Plan/IPA that the member&#8217;s health plan coverage is still in effect. {getDisplayCompanyId(props.authorizationDetailModal.RmiCompanyId)} reserves
                        the right to revoke this authorization prior to services being rendered based on cancellation of the member&#8217;s eligibility.
                        Final determination of benefits will be made after review of the claim for limitations or exclusions.
                      </>
                    }
                    type="info"
                    showIcon
                  />
                </div>
              </div>
            </>
          )}
        </div>

      </Modal>
    </>
  );
};

export default AuthorizationDetailModal;